import React, { useState } from 'react'
import { motion } from 'framer-motion'
import emailjs from "@emailjs/browser";
import Swal from 'sweetalert2'
import './Contact.css'

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    marketingConsent: false
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const serviceID = "service_3uorpq1";
    const templateID = "template_9nwpbbc";
    const publicKey = "HDJhIu1QvInQD-bU0";

    // Show sending alert
    Swal.fire({
      title: 'Sending Message...',
      text: 'Please wait while we send your message.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading()
      }
    });

    // Ensure template params match EmailJS template
    const templateParams = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      marketingConsent: formData.marketingConsent ? "Yes" : "No",
      reply_to: formData.email,  // Use this instead!
    };

    try {
      const response = await emailjs.send(serviceID, templateID, templateParams, { publicKey });
      console.log("Email sent successfully!", response);
      
      // Success alert
      await Swal.fire({
        icon: 'success',
        title: 'Message Sent!',
        text: 'Thank you for contacting us. We\'ll get back to you soon!',
        confirmButtonColor: '#059669',
        confirmButtonText: 'Great!'
      });

      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        marketingConsent: false,
      });
    } catch (error) {
      console.error("Error sending email:", error);
      
      // Error alert
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to send message. Please try again later.',
        confirmButtonColor: '#dc2626',
        confirmButtonText: 'Try Again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  }

  return (
    <div className="contact-wrapper">
      <motion.div 
        className="contact-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="contact-header">
          <motion.h1 
            className="contact-title"
            {...fadeIn}
            transition={{ delay: 0.2 }}
          >
            Contact Us
          </motion.h1>
          <motion.p 
            className="contact-description"
            {...fadeIn}
            transition={{ delay: 0.3 }}
          >
            Have questions or need assistance? We're here to help. Fill out the form below or reach out to us directly.
          </motion.p>
        </div>

        <motion.form 
          onSubmit={handleSubmit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="contact-form">
            <motion.div 
              className="form-group"
              {...fadeIn}
              transition={{ delay: 0.5 }}
            >
              <input
                type="text"
                name="firstName"
                className="form-input"
                placeholder="First name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={isSubmitting}
                required
              />
            </motion.div>

            <motion.div 
              className="form-group"
              {...fadeIn}
              transition={{ delay: 0.6 }}
            >
              <input
                type="text"
                name="lastName"
                className="form-input"
                placeholder="Last name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={isSubmitting}
                required
              />
            </motion.div>

            <motion.div 
              className="form-group"
              {...fadeIn}
              transition={{ delay: 0.7 }}
            >
              <input
                type="email"
                name="email"
                className="form-input"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
                required
              />
            </motion.div>

            <motion.div 
              className="form-group"
              {...fadeIn}
              transition={{ delay: 0.8 }}
            >
              <input
                type="tel"
                name="phone"
                className="form-input"
                placeholder="Phone number"
                value={formData.phone}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </motion.div>

            <motion.div 
              className="form-group full-width"
              {...fadeIn}
              transition={{ delay: 0.9 }}
            >
              <textarea
                name="message"
                className="form-input"
                placeholder="Your message"
                value={formData.message}
                onChange={handleChange}
                disabled={isSubmitting}
                required
                rows={4}
              />
            </motion.div>
          </div>

          <motion.div 
            className="checkbox-group"
            {...fadeIn}
            transition={{ delay: 1.1 }}
          >
            <input
              type="checkbox"
              id="marketingConsent"
              name="marketingConsent"
              className="checkbox-input"
              checked={formData.marketingConsent}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <label htmlFor="marketingConsent" className="checkbox-label">
            We'll use your personal information only to communicate and handle your inquiry.
            </label>
          </motion.div>

          <div className="button-container">
            <button 
              type="submit" 
              disabled={isSubmitting}
              className={`inline-block px-8 py-3 rounded-full transition-all duration-300 text-white ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-emerald-600/80 backdrop-blur-sm hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-500/20'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </motion.form>
      </motion.div>
    </div>
  )
}

export default ContactForm